/**
 * Utility functions for generating and parsing semantic share URLs
 * Format: /snippet/{prefix}-{shortId}
 * Example: /snippet/ctojs-0fcb7df0 (C → JavaScript)
 */

/**
 * Generate a semantic prefix from language pair
 * @param sourceLanguage - Source language (e.g., "C")
 * @param targetLanguage - Target language (e.g., "JavaScript")
 * @returns Semantic prefix (e.g., "ctojs")
 */
export function generateSharePrefix(sourceLanguage: string, targetLanguage: string): string {
  // Normalize language names to lowercase
  const source = sourceLanguage.toLowerCase()
  const target = targetLanguage.toLowerCase()

  // Common language abbreviations
  const abbreviations: Record<string, string> = {
    'javascript': 'js',
    'typescript': 'ts',
    'python': 'py',
    'c': 'c',
    'c++': 'cpp',
    'c#': 'cs',
    'java': 'java',
    'go': 'go',
    'rust': 'rs',
    'ruby': 'rb',
    'php': 'php',
    'swift': 'swift',
    'kotlin': 'kt',
    'scala': 'scala',
    'r': 'r',
    'julia': 'jl',
    'objective-c': 'objc',
    'sql': 'sql',
    'shell': 'sh',
    'bash': 'bash'
  }

  // Get abbreviations or use first 2-4 chars
  const sourceAbbr = abbreviations[source] || source.substring(0, source.length <= 4 ? source.length : 2)
  const targetAbbr = abbreviations[target] || target.substring(0, target.length <= 4 ? target.length : 2)

  return `${sourceAbbr}to${targetAbbr}`
}

/**
 * Parse a prefixed share ID to extract the UUID short ID
 * @param prefixedId - Prefixed ID (e.g., "ctojs-0fcb7df0" or just "0fcb7df0")
 * @returns Short UUID ID (e.g., "0fcb7df0")
 */
export function parseShareId(prefixedId: string): string {
  // If contains a dash, extract the part after the last dash
  const lastDashIndex = prefixedId.lastIndexOf('-')
  if (lastDashIndex !== -1) {
    return prefixedId.substring(lastDashIndex + 1)
  }

  // Otherwise, assume it's already just the short ID
  return prefixedId
}

/**
 * Generate a complete share URL with semantic prefix
 * @param shareId - Full UUID
 * @param sourceLanguage - Source language
 * @param targetLanguage - Target language
 * @param baseUrl - Base URL (defaults to window.location.origin)
 * @returns Complete share URL (e.g., "http://localhost:3005/snippet/ctojs-0fcb7df0")
 */
export function generateShareUrl(
  shareId: string,
  sourceLanguage: string,
  targetLanguage: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): string {
  const prefix = generateSharePrefix(sourceLanguage, targetLanguage)
  const shortId = shareId.substring(0, 8)
  return `${baseUrl}/snippet/${prefix}-${shortId}`
}

/**
 * Extract language context from a prefixed share ID
 * @param prefixedId - Prefixed ID (e.g., "ctojs-0fcb7df0")
 * @returns Object with prefix and shortId, or null if no prefix
 */
export function extractShareContext(prefixedId: string): { prefix: string; shortId: string } | null {
  const lastDashIndex = prefixedId.lastIndexOf('-')
  if (lastDashIndex !== -1) {
    const prefix = prefixedId.substring(0, lastDashIndex)
    const shortId = prefixedId.substring(lastDashIndex + 1)
    return { prefix, shortId }
  }

  return null
}
